import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Chip, Button, Avatar, AvatarGroup,
  LinearProgress, Skeleton, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Switch, FormControlLabel,
} from '@mui/material';
import { LocationOn, AccessTime, Group, Share, Star, QrCode2, Coffee } from '@mui/icons-material';
import eventsService, { EventItem } from '../../services/eventsService';
import vouchersService from '../../services/vouchersService';
import userService from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { usePageMeta } from '../../hooks/usePageMeta';
import { colors, designTokens } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  const [event, setEvent] = useState<EventItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [useCredits, setUseCredits] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSafetyPrompt, setShowSafetyPrompt] = useState(false);
  const [acknowledgingSafety, setAcknowledgingSafety] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState<{ short_code: string; voucher_id: string } | null>(null);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  usePageMeta({
    title: event ? `${event.title} — CoffeeMates` : undefined,
    description: event?.description || (event ? `Join ${event.title} on CoffeeMates` : undefined),
    image: event?.venue?.photo_url,
    url: event ? `https://coffeemates.biz/events/${event.id}` : undefined,
  });

  useEffect(() => {
    if (!id) return;
    eventsService.getDetail(id).then(e => { setEvent(e); setLoading(false); }).catch((e) => { console.warn('[EventDetailPage] load event failed', e); setError(e.message || 'Failed to load event'); setLoading(false); });
  }, [id]);

  const handlePurchase = async () => {
    if (!event) return;
    setPurchasing(true);
    setDialogError('');
    try {
      const result = await vouchersService.purchase(event.id, selectedTier || event.voucher_tier, useCredits);
      setPurchaseResult({ short_code: result.short_code, voucher_id: result.voucher_id });
      setSuccess('purchased');
      setShowPayment(false);
      setDialogError('');
      const updated = await eventsService.getDetail(event.id);
      setEvent(updated);
    } catch (e: any) {
      console.warn('[EventDetailPage] purchase failed', e);
      const msg = e.message || 'Purchase failed';
      if (msg.toLowerCase().includes('safety promise') || msg.toLowerCase().includes('safety')) {
        setShowSafetyPrompt(true);
        setShowPayment(false);
      } else {
        setDialogError(msg);
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleAcknowledgeSafety = async () => {
    setAcknowledgingSafety(true);
    try {
      await userService.acknowledgeSafety();
      setShowSafetyPrompt(false);
      setShowPayment(true); // Re-open purchase dialog
    } catch (e: any) {
      console.warn('[EventDetailPage] acknowledge safety failed', e);
      setDialogError(e.message || 'Failed to acknowledge');
    } finally {
      setAcknowledgingSafety(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 5, mb: 3 }} animation="wave" />
        <Skeleton variant="rounded" height={40} width={300} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={20} width={200} sx={{ mb: 1, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={20} width={250} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>Event not found</Alert>
      </Container>
    );
  }

  const isPro = event.event_type === 'professional';
  const venueSupportsPremium = event.venue?.tier === 'premium';
  const activeTier = selectedTier || event.voucher_tier;
  const isMasterclass = event.event_type === 'masterclass';
  const mcFee = event.masterclass_fee || 0;
  const voucherPrice = activeTier === 'premium' ? 13 : 10;
  const price = voucherPrice + mcFee;
  const fillPercent = (event.current_count / event.max_capacity) * 100;
  const capacityColor = event.is_full ? colors.error.main : event.spots_remaining <= 2 ? colors.warning.dark : colors.secondary.main;

  return (
    <Box sx={{ pb: 12 }}>
      {/* ── Hero Image — Full-width, immersive ── */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: { xs: 240, md: 320 },
        }}
      >
        <img
          src={event.venue?.photo_url || 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=800'}
          alt={event.venue?.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(transparent 40%, rgba(42,33,24,0.4) 100%)',
          }}
        />
        {/* Badges */}
        <Box sx={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 0.5 }}>
          <Chip
            label={isPro ? 'Professional' : 'Social'}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              color: isPro ? colors.primary.main : colors.accent.dark,
              fontWeight: 700,
              borderRadius: 8,
            }}
          />
          {event.voucher_tier === 'premium' && (
            <Chip label="Premium" size="small" sx={{
              bgcolor: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(8px)',
              color: colors.secondary.dark,
              fontWeight: 700,
              borderRadius: 8,
            }} />
          )}
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 1 }}>
        {/* ── Main Content Card ── */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 5,
            border: `1px solid ${designTokens.glass.border}`,
            bgcolor: designTokens.glass.surface,
            backdropFilter: designTokens.glass.blur,
            mb: 3,
          }}
        >
          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: colors.neutral[900],
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {event.title}
          </Typography>

          {/* Meta info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {event.venue && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 8, bgcolor: colors.primary.transparent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LocationOn sx={{ fontSize: 17, color: colors.primary.main }} />
                </Box>
                <Box sx={{ cursor: 'pointer' }} onClick={() => event.venue?.slug && navigate(`/venues/${event.venue.slug}`)}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.main, '&:hover': { textDecoration: 'underline' } }}>
                    {event.venue.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.neutral[400] }}>
                    {event.venue.address}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: 8, bgcolor: colors.secondary.transparent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccessTime sx={{ fontSize: 17, color: colors.secondary.main }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {event.scheduled_date} at {event.scheduled_time?.slice(0, 5)}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.neutral[400] }}>
                  {event.duration_minutes} minutes
                </Typography>
              </Box>
            </Box>

            {event.avg_rating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 8, bgcolor: colors.accent.subtle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Star sx={{ fontSize: 17, color: colors.accent.main }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                  {event.avg_rating.toFixed(1)} <Typography component="span" variant="caption" sx={{ color: colors.neutral[400] }}>({event.rating_count} ratings)</Typography>
                </Typography>
              </Box>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Description */}
          {(event.description || event.topic_brief) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.neutral[800], mb: 1 }}>
                {isPro ? t('events.topic_brief') : t('events.event_detail')}
              </Typography>
              <Typography sx={{ color: colors.neutral[500], lineHeight: 1.7 }}>
                {event.topic_brief || event.description}
              </Typography>
              {event.game_activity && (
                <Chip
                  label={event.game_activity}
                  sx={{
                    mt: 1.5,
                    bgcolor: colors.accent.subtle,
                    color: colors.accent.dark,
                    fontWeight: 600,
                    borderRadius: 10,
                  }}
                />
              )}
            </Box>
          )}
        </Paper>

        {/* ── Purchase Success ── */}
        {purchaseResult && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              textAlign: 'center',
              bgcolor: colors.success.subtle,
              border: `1px solid ${colors.success.light}`,
              borderRadius: 5,
            }}
          >
            <Box sx={{ width: 56, height: 56, borderRadius: 14, bgcolor: designTokens.glass.surface, border: `1px solid ${designTokens.glass.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Coffee sx={{ fontSize: 28, color: colors.success.main }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.success.dark, mb: 0.5 }}>
              You're in!
            </Typography>
            <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 3 }}>
              Show this code to the barista or use the QR code
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: '0.2em',
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                color: colors.primary.main,
                bgcolor: designTokens.glass.surface,
                py: 2, px: 3,
                borderRadius: 3,
                display: 'inline-block',
                mb: 3,
                fontSize: '1.8rem',
                border: `1px solid ${designTokens.glass.border}`,
              }}
            >
              {purchaseResult.short_code}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<QrCode2 />}
                onClick={() => navigate(`/vouchers/${purchaseResult.voucher_id}/qr`)}
                sx={{ borderRadius: 3, px: 4, bgcolor: colors.success.main, '&:hover': { bgcolor: colors.success.dark } }}
              >
                {t('voucher.show_qr')}
              </Button>
              <Button variant="outlined" size="large"
                onClick={() => navigate(`/waiting/${purchaseResult.voucher_id}`)}
                sx={{ borderRadius: 3, px: 4 }}>
                Go to Waiting Room
              </Button>
            </Box>
          </Paper>
        )}

        {/* ── Capacity Section ── */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group sx={{ color: colors.neutral[400], fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.neutral[800] }}>
                {event.current_count}/{event.max_capacity} people
              </Typography>
            </Box>
            <Chip
              label={event.is_full ? t('events.full') : t('events.spots_left', { count: event.spots_remaining })}
              size="small"
              sx={{
                bgcolor: event.is_full ? colors.error.subtle : colors.success.subtle,
                color: capacityColor,
                fontWeight: 700,
                borderRadius: 8,
              }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={fillPercent}
            sx={{
              height: 6,
              borderRadius: 4,
              bgcolor: 'rgba(255,255,255,0.06)',
              mb: 2,
              '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: capacityColor },
            }}
          />

          {event.attendees && event.attendees.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: colors.neutral[400], mb: 1, display: 'block' }}>
                {t('events.whos_coming')}
              </Typography>
              <AvatarGroup max={8} sx={{ justifyContent: 'flex-start' }}>
                {event.attendees.map((a, i) => (
                  <Avatar key={i} src={a.avatar_url || undefined} sx={{ width: 36, height: 36, bgcolor: colors.secondary.light }}>
                    {a.first_name[0]}
                  </Avatar>
                ))}
              </AvatarGroup>
            </Box>
          )}
        </Paper>

        {/* ── Venue Menu ── */}
        {event.venue?.coffeemates_menu && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              mb: 3,
              borderRadius: 4,
              border: `1px solid ${colors.neutral[200]}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.neutral[800], mb: 2 }}>
              {t('events.whats_included')}
            </Typography>
            {Object.entries(event.venue.coffeemates_menu).map(([tier, items]: [string, any]) => (
              <Box key={tier} sx={{ mb: 1.5 }}>
                <Typography
                  variant="overline"
                  sx={{ color: colors.neutral[500], display: 'block', mb: 0.5 }}
                >
                  {tier} tier
                </Typography>
                {(items as any[]).map((item: any, i: number) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: colors.neutral[600] }}>{item.name}</Typography>
                    <Typography variant="body2" sx={{ color: colors.neutral[400], fontWeight: 600 }}>{item.price} AZN</Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Paper>
        )}
      </Container>

      {/* ── Sticky Purchase Bar ── */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          bgcolor: 'rgba(15,15,19,0.92)',
          backdropFilter: 'blur(24px)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          py: 1.5,
          px: 2,
        }}
      >
        <Container maxWidth="md" sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {/* Price display */}
          <Box sx={{ flex: '0 0 auto', mr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: colors.neutral[900] }}>
                {price}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: colors.neutral[400] }}>
                AZN
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: colors.neutral[400] }}>per person</Typography>
          </Box>

          {/* Action button */}
          <Box sx={{ flex: 1 }}>
            {event.user_has_voucher && !success ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled
                sx={{
                  py: 1.5,
                  borderRadius: 14,
                  '&.Mui-disabled': {
                    bgcolor: colors.success.subtle,
                    color: colors.success.dark,
                    border: `1px solid ${colors.success.light}`,
                  },
                }}
              >
                You're going!
              </Button>
            ) : event.is_full ? (
              <Box>
                {event.waitlist_count > 0 && (
                  <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: colors.warning.dark, fontWeight: 600 }}>
                    🔥 {event.waitlist_count} {event.waitlist_count === 1 ? 'person' : 'people'} on the waitlist
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  sx={{ py: 1.5, borderRadius: 14 }}
                  onClick={() => isAuthenticated ? eventsService.joinWaitlist(event.id) : navigate('/login')}
                >
                  Join waitlist{event.waitlist_count > 0 ? ` (position #${event.waitlist_count + 1})` : ''}
                </Button>
              </Box>
            ) : success ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ py: 1.5, borderRadius: 14 }}
                onClick={() => navigate('/vouchers')}
              >
                View my vouchers
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  py: 1.5,
                  borderRadius: 14,
                  fontSize: '1rem',
                  fontWeight: 700,
                }}
                onClick={() => isAuthenticated ? (setDialogError(''), setShowPayment(true)) : navigate('/login')}
              >
                {t('events.join')}
              </Button>
            )}
          </Box>

          {/* Share */}
          <Button
            variant="outlined"
            sx={{
              minWidth: 48,
              width: 48,
              height: 48,
              borderRadius: 14,
              p: 0,
              borderColor: colors.neutral[200],
              color: colors.neutral[500],
            }}
            onClick={() => eventsService.shareEvent(event.id)}
          >
            <Share sx={{ fontSize: 20 }} />
          </Button>
        </Container>
      </Box>

      {/* ── Payment Dialog — Clean, minimal ── */}
      <Dialog
        open={showPayment}
        onClose={() => setShowPayment(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.neutral[800], pb: 0 }}>
          Confirm your seat
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ color: colors.neutral[400], mb: 2 }}>{event.title}</Typography>
            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
            <Divider sx={{ mb: 2 }} />

            {venueSupportsPremium ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF', mb: 1.5 }}>
                  {t('voucher.tier_select')}
                </Typography>
                {[
                  { tier: 'standard', price: 10, desc: 'Cappuccino, Latte, or Americano' },
                  { tier: 'premium', price: 13, desc: 'Specialty coffee + dessert' },
                ].map((opt) => (
                  <Paper
                    key={opt.tier}
                    onClick={() => setSelectedTier(opt.tier)}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 1,
                      cursor: 'pointer',
                      borderRadius: 3,
                      border: `2px solid ${activeTier === opt.tier ? colors.primary.main : designTokens.glass.border}`,
                      bgcolor: activeTier === opt.tier ? colors.primary.subtle : designTokens.glass.surface,
                      transition: `all ${designTokens.transition.fast}`,
                      '&:hover': {
                        borderColor: activeTier === opt.tier ? colors.primary.main : colors.neutral[300],
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', color: colors.neutral[800] }}>
                          {opt.tier}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.neutral[400] }}>{opt.desc}</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary.main }}>
                        {opt.price} AZN
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: colors.neutral[600] }}>Voucher ({event.voucher_tier})</Typography>
                <Typography fontWeight={600}>{price} AZN</Typography>
              </Box>
            )}

            <Typography variant="caption" sx={{ color: colors.neutral[400], display: 'block', mb: 1 }}>
              Includes beverage + 1 AZN service fee
              {isMasterclass && ` + ${mcFee} AZN master class fee`}
            </Typography>
            {user && user.wallet_balance > 0 && (
              <FormControlLabel
                control={<Switch checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} size="small" />}
                label={`Use wallet credits (${user.wallet_balance} AZN)`}
                sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: colors.neutral[600] } }}
              />
            )}
            {/* Cancellation policy summary */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#FFFFFF', display: 'block', mb: 1 }}>
                Your purchase includes:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.78rem', color: colors.neutral[500], mb: 0.3 } }}>
                <li>✅ Free cancellation before event locks (full refund)</li>
                <li>🔄 One-time event switch to a different table</li>
                <li>🎫 Withdraw & reuse voucher within 30 days</li>
                <li>🛡️ CoffeeMates Safety Promise</li>
              </Box>
              <Button
                size="small"
                onClick={() => window.open('/terms#cancellation', '_blank')}
                sx={{ mt: 1, fontSize: '0.72rem', color: colors.primary.main, textTransform: 'none', p: 0 }}
              >
                View full cancellation & refund policy →
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setShowPayment(false)} sx={{ color: colors.neutral[400] }}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handlePurchase}
            disabled={purchasing}
            sx={{ minWidth: 140, borderRadius: 12 }}
          >
            {purchasing ? 'Processing...' : `Pay ${price} AZN`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Safety Promise Acknowledgment Dialog */}
      <Dialog open={showSafetyPrompt} onClose={() => setShowSafetyPrompt(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: colors.neutral[800] }}>
          🛡️ CoffeeMates Safety Promise
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Before joining your first event, please acknowledge our community safety commitment:
          </Typography>
          <Paper sx={{ p: 2.5, bgcolor: colors.primary.subtle, borderRadius: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
              <strong>CoffeeMates is committed to providing a safe, respectful space for everyone.</strong>
              {' '}We operate a zero-tolerance policy for:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, '& li': { mb: 0.5 } }}>
              <li><Typography variant="body2">Harassment of any kind</Typography></li>
              <li><Typography variant="body2">Intoxication (alcohol or drugs)</Typography></li>
              <li><Typography variant="body2">Threatening or abusive behavior</Typography></li>
              <li><Typography variant="body2">Stalking or unwanted contact after events</Typography></li>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Violations result in immediate and permanent removal from the platform.
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            By clicking "I Agree", you confirm that you've read and will follow these guidelines at every CoffeeMates event.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowSafetyPrompt(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAcknowledgeSafety} disabled={acknowledgingSafety}
            sx={{ bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.dark } }}>
            {acknowledgingSafety ? 'Saving...' : t('safety.agree')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
